
import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProcessSection from '../components/ProcessSection';

const {FiUsers, FiTarget, FiTrendingUp, FiSearch, FiShield, FiAward, FiCheckCircle, FiCalendar} = FiIcons;

const Services = () => {
  const services = [
    {
      icon: FiUsers,
      title: 'Recruitment Services',
      description: 'Find exceptional talent across all levels of your organization',
      features: [
        'Permanent placement for technical roles',
        'Contract and interim positions',
        'Executive search for leadership roles',
        'Graduate and early career recruitment',
        'Specialized robotics and AI talent sourcing'
      ],
      process: [
        'Intake consultation to understand your needs',
        'Targeted candidate sourcing and screening',
        'Technical and cultural fit assessment',
        'Interview coordination and feedback',
        'Offer negotiation and onboarding support'
      ]
    },
    {
      icon: FiTarget,
      title: 'HR Consulting',
      description: 'Strategic HR support for fast-scaling tech companies',
      features: [
        'Interim HR leadership',
        'Organizational structure design',
        'Compliance and legal requirements',
        'Compensation and benefits strategy',
        'Performance management systems'
      ],
      process: [
        'HR audit and gap analysis',
        'Strategic roadmap development',
        'Implementation support',
        'Ongoing advisory and optimization',
        'Knowledge transfer and training'
      ]
    },
    {
      icon: FiTrendingUp,
      title: 'Team Development',
      description: 'Build high-performing, cohesive teams',
      features: [
        'Leadership coaching and development',
        'Team alignment workshops',
        'Performance optimization',
        'Communication and collaboration training',
        'Conflict resolution and mediation'
      ],
      process: [
        'Team assessment and diagnostics',
        'Customized development program design',
        'Workshop delivery and coaching sessions',
        'Progress tracking and feedback',
        'Continuous improvement support'
      ]
    }
  ];

  const industries = [
    {
      name: 'Robotics & Automation',
      description: 'Industrial robots, service robots, autonomous systems'
    },
    {
      name: 'Computer Vision',
      description: 'Image processing, machine learning, visual AI'
    },
    {
      name: 'Artificial Intelligence',
      description: 'ML/AI platforms, NLP, predictive analytics'
    },
    {
      name: 'Industrial Tech',
      description: 'Manufacturing, automotive, agriculture tech'
    },
    {
      name: 'SaaS & Software',
      description: 'Technical software platforms and tools'
    }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-600 to-primary-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Comprehensive Talent Solutions
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
              From recruitment to team development, we provide end-to-end people solutions for deep tech companies.
            </p>
            <a
              href="https://tidycal.com/trbe/exploration-call"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-brand-orange text-white px-8 py-4 rounded-lg font-semibold hover:bg-secondary-500 transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <SafeIcon icon={FiCalendar} className="h-5 w-5" />
              <span>Book a Call</span>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <ProcessSection />

      {/* Services Detail */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-20">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg"
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <div>
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-secondary-100 rounded-xl flex items-center justify-center">
                        <SafeIcon icon={service.icon} className="h-8 w-8 text-primary-600" />
                      </div>
                      <h2 className="text-3xl font-bold text-neutral-900">{service.title}</h2>
                    </div>
                    <p className="text-lg text-neutral-600 mb-8">{service.description}</p>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-4">What We Offer:</h3>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-secondary-500 mt-0.5 flex-shrink-0" />
                          <span className="text-neutral-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-neutral-900 mb-6">Our Process:</h3>
                    <div className="space-y-4">
                      {service.process.map((step, idx) => (
                        <div key={idx} className="flex items-start space-x-4">
                          <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                            {idx + 1}
                          </div>
                          <div className="pt-1">
                            <span className="text-neutral-700">{step}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl font-bold text-neutral-900 mb-4">
              Industries We Serve
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Deep expertise across the most innovative sectors in technology.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-neutral-50 rounded-xl p-6 hover:bg-primary-50 transition-colors duration-300"
              >
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">{industry.name}</h3>
                <p className="text-neutral-600">{industry.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Hiring?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
              Let's discuss how our services can help you build exceptional teams.
            </p>
            <a
              href="https://tidycal.com/trbe/exploration-call"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-medium hover:bg-primary-50 transition-colors duration-200"
            >
              Schedule a Consultation
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
